import { createContext, useContext, type ReactNode } from "react";
import Close from "@mui/icons-material/Close";
import Done from "@mui/icons-material/Done";
import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { toast, ToastContainer } from "react-toastify";

import "react-toastify/ReactToastify.css";

// TODO(extraction): eMHA's `renderSuccessToast` in aspire-frontend draws the
// same toast. Move both onto one copy in thalamos-frontend.

// eMHA's toast, drawn as it is rather than from Haven's theme: its colours,
// sizes and close-icon placement.
const toastBackgroundColour = "#F2FCF9";
const toastBorderColour = "#00BF7C";
const doneIconColour = "#8ABE23";
const closeIconColour = "#554455";
const messageFontSize = "13px";

// eMHA's dwell time. Hovering over a toast pauses it.
const toastDurationMs = 30_000;

/**
 * How a page raises a toast. A toast confirms something the person has just done, where
 * the page they land on does not make it obvious. A warning, an error or a
 * question stays on the page or in a dialog, which is why there is one kind.
 */
export type Toaster = {
  readonly show: (message: string) => void;
};

// Closing dismisses every toast showing, through an icon rather than a
// button, as eMHA's does: Haven's toasts match eMHA's exactly, and any change
// to them is made across the products together.
const ToastContent = ({ message }: { message: string }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      px: 3,
      py: 2,
      gap: 2,
    }}
  >
    <Done sx={{ color: doneIconColour }} />
    <Typography
      sx={{
        fontWeight: 400,
        fontSize: messageFontSize,
        color: "text.primary",
      }}
    >
      {message}
    </Typography>
    <Box sx={{ m: 2 }}>
      <Box
        sx={{
          position: "absolute",
          right: (theme) => theme.spacing(2),
          transform: "translate(-45%, -45%)",
        }}
      >
        <Close
          fontSize="small"
          sx={{ color: closeIconColour, "&:hover": { cursor: "pointer" } }}
          onClick={() => {
            toast.dismiss();
          }}
        />
      </Box>
    </Box>
  </Box>
);

const toaster: Toaster = {
  show: (message) => {
    toast.success(<ToastContent message={message} />, {
      autoClose: toastDurationMs,
      closeButton: false,
      icon: false,
      style: {
        backgroundColor: toastBackgroundColour,
        border: `2px solid ${toastBorderColour}`,
        borderRadius: "6px",
      },
    });
  },
};

const ToastContext = createContext<Toaster | undefined>(undefined);

/**
 * Draws the toasts raised beneath it. A provider inside another adds nothing,
 * so a story or test may supply one around a page that already has one.
 */
export const ToastProvider = ({ children }: { children: ReactNode }) => {
  const outer = useContext(ToastContext);
  const { t } = useTranslation();
  if (outer) {
    return children;
  }

  return (
    <ToastContext.Provider value={toaster}>
      {children}
      <Box
        sx={{
          width: "100%",
          "& .Toastify__toast-container": {
            width: "auto",
            maxWidth: 450,
          },
          "& .Toastify__toast": {
            p: 0,
            borderRadius: 0,
            mb: 2.5,
            minHeight: 0,
          },
        }}
      >
        <ToastContainer
          aria-label={t("toasts.region")}
          position="top-right"
          hideProgressBar
          closeOnClick={false}
          draggable={false}
          pauseOnHover
          style={{ backgroundColor: "transparent" }}
        />
      </Box>
    </ToastContext.Provider>
  );
};

export const useToast = (): Toaster => {
  const provided = useContext(ToastContext);
  if (!provided) {
    throw new Error("useToast needs a ToastProvider above it");
  }
  return provided;
};
